import { useEffect, useMemo, useState } from "react";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

type Check = { formula: boolean; price: boolean; packaging: boolean; retail: boolean };
type Row = {
  id: string; productNumber: string; name: string; nameAr: string | null;
  kind: "original" | "promoted"; checks: Check; ready: boolean;
  cogs: number | null; retail: number | null; needsPriceCount: number; unmappedCount: number;
};

const CHECKS: { key: keyof Check; label: string }[] = [
  { key: "formula", label: "التركيبة" },
  { key: "price", label: "أسعار الخامات" },
  { key: "packaging", label: "التغليف" },
  { key: "retail", label: "سعر البيع" },
];

const Tick = ({ ok, testId }: { ok: boolean; testId: string }) => (
  <span data-testid={testId} style={{ color: ok ? "var(--bl-ok)" : "var(--bl-danger)", fontWeight: 800 }}>
    {ok ? "✓" : "✗"}
  </span>
);

export default function ReadinessPage() {
  const [market, setMarket] = useState<"eg" | "sa">("eg");
  const [data, setData] = useState<{ total: number; readyCount: number; rows: Row[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);

  useEffect(() => {
    setData(null);
    fetch(`${API}/launch-readiness?marketId=${market}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(setData).catch((e) => setErr(String(e.message || e)));
  }, [market]);

  const rows = useMemo(() => {
    if (!data) return [];
    return onlyMissing ? data.rows.filter((r) => !r.ready) : data.rows;
  }, [data, onlyMissing]);

  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="readiness-error">{err}</div>;
  if (!data) return <div className="bl-sub py-8 text-center" data-testid="readiness-loading">جارٍ حساب الجاهزية…</div>;

  const cur = market === "eg" ? "EGP" : "SAR";
  return (
    <div className="space-y-4" data-testid="readiness-page">
      <div className="bl-card">
        <div className="bl-eyebrow mb-2">🚀 جاهزية الإطلاق — إيه اللي ناقص كل منتج علشان يتباع</div>
        <div className="flex gap-2 items-center flex-wrap mb-3">
          <button className={`bl-tab ${market === "eg" ? "active" : ""}`} onClick={() => setMarket("eg")} data-testid="readiness-market-eg">مصر · EGP</button>
          <button className={`bl-tab ${market === "sa" ? "active" : ""}`} onClick={() => setMarket("sa")} data-testid="readiness-market-sa">السعودية · SAR</button>
          <span className="bl-pill" data-testid="readiness-summary"
            style={{ color: data.readyCount === data.total ? "var(--bl-ok)" : undefined }}>
            جاهز للبيع: {data.readyCount} / {data.total}
          </span>
          <button className={`bl-tab ${onlyMissing ? "active" : ""}`} onClick={() => setOnlyMissing(!onlyMissing)}
            data-testid="readiness-only-missing">الناقص بس ({data.total - data.readyCount})</button>
        </div>
        <table className="bl-table" data-testid="readiness-table">
          <thead>
            <tr>
              <th>المنتج</th>
              {CHECKS.map((c) => <th key={c.key}>{c.label}</th>)}
              <th>COGS خامات</th><th>سعر البيع</th><th>جاهز؟</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`readiness-row-${r.name}`}
                style={r.ready ? { background: "rgba(107,187,146,0.05)" } : undefined}>
                <td>
                  <span className="bl-en font-extrabold">{r.name}</span>{" "}
                  <span className="bl-sub">{r.nameAr}</span>{" "}
                  <span className="bl-sub bl-en">{r.productNumber}</span>{" "}
                  {r.kind === "original" && <span className="bl-pill">أصلي 🔒</span>}
                </td>
                {CHECKS.map((c) => (
                  <td key={c.key}>
                    <Tick ok={r.checks[c.key]} testId={`readiness-${c.key}-${r.name}`} />
                    {c.key === "price" && !r.checks.price && r.needsPriceCount > 0 && (
                      <span className="bl-sub mr-1">({r.needsPriceCount} خامة)</span>
                    )}
                  </td>
                ))}
                <td>
                  <span className="bl-pill" data-testid={`readiness-ifra-${r.name}`}
                    style={(r as any).ifraCovered ? { color: "var(--bl-ok)" } : { opacity: 0.6 }}>
                    {(r as any).ifraCovered ? "مغطّى IFRA ✓" : "غير مغطّى"}
                  </span>
                </td>
                <td className="bl-en">{r.cogs != null ? `${r.cogs} ${cur}` : "—"}</td>
                <td className="bl-en">{r.retail != null ? `${r.retail} ${cur}` : "—"}</td>
                <td>
                  <span className={`bl-pill ${r.ready ? "" : "danger"}`} data-testid={`readiness-ready-${r.name}`}
                    style={r.ready ? { color: "var(--bl-ok)" } : undefined}>
                    {r.ready ? "جاهز ✓" : "ناقص"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="bl-sub mt-2" style={{ fontSize: 10 }}>
          أسعار الخامات بتتملى من المخزون (شيت الأسعار) · سعر البيع للمُرقّى من لوحة الهامش · التغليف من بيانات السوق في Product Master.
        </p>
      </div>
    </div>
  );
}
